import { useLocation } from 'react-router';
import GlobalContext from './GlobalContext';
import API from '../api/api';
import { createContext, FC, useContext, useEffect, useState } from 'react';
import { TRequestVerifyPost } from '../api/types';
import { EQueryStringParam } from '../util/constants';

export type TUnverifiedRouterState = {
    userId: string;
    email: string;
};

export type TUnverifiedAccountContext = {
    email: string;
    unverifiedAccountUiState: EUnverifiedAccountUiState;
    requestVerificationEmail: () => Promise<void>;
    submitVerifyPasscode: (passcode: string) => Promise<void>;
    resendVerifyPasscode: () => Promise<void>;
    showToaster: boolean;
};

export enum EUnverifiedAccountUiState {
    default,
    routerParsed,
    loading,
    error,
    success,
}

const UnverifiedAccounContext = createContext<TUnverifiedAccountContext | undefined>(undefined);

export const UnverifiedAccounProvider: FC = ({ children }) => {
    const { gatewayAPIBaseUrl, putFullLoginLinkInLocalStorage, subscriberId } = useContext(GlobalContext);
    const [unverifiedAccountUiState, setUnverifiedAccountUiState] = useState(EUnverifiedAccountUiState.default);
    const [showToaster, setShowToaster] = useState(false);
    const [email, setEmail] = useState('');
    const [userId, setUserId] = useState('');
    const { state: routerState }: { state: TUnverifiedRouterState } = useLocation();

    const parseRouterState = () => {
        if (routerState) {
            const { email, userId } = routerState;
            if (email && userId) {
                setEmail(email);
                setUserId(userId);
                setUnverifiedAccountUiState(EUnverifiedAccountUiState.routerParsed);
            } else {
                console.error(
                    `
                    Failed to parse router state: 
                    ${EQueryStringParam.userId} = ${userId ? userId : 'null'}
                    ${EQueryStringParam.email} = ${email ? email : 'null'}
                    `,
                );
                setUnverifiedAccountUiState(EUnverifiedAccountUiState.error);
            }
        }
    };

    const requestVerificationEmail = async () => {
        setUnverifiedAccountUiState(EUnverifiedAccountUiState.loading);
        const postObject = { email, ...(subscriberId && { subscriberId }) };
        try {
            await API.post.requestVerify(gatewayAPIBaseUrl, postObject);
            setUnverifiedAccountUiState(EUnverifiedAccountUiState.success);
        } catch (error) {
            setUnverifiedAccountUiState(EUnverifiedAccountUiState.error);
        }
    };

    const submitVerifyPasscode = async (passcode: string) => {
        const queryParams = `?code=${passcode}&subscriberId=${subscriberId}&userId=${userId}`;
        try {
            await API.get.verifyAccount(queryParams);
        } catch (error) {
            console.error('Failed to verify passcode');
            throw error.response?.status;
        }
    };

    const resendVerifyPasscode = async () => {
        const postObject: TRequestVerifyPost = {
            email,
            userId,
            usePasscode: true,
            ...(subscriberId && { subscriberId }),
        };
        try {
            await API.post.requestVerify(gatewayAPIBaseUrl, postObject);
        } catch (error) {
            console.error('Failed to resend passcode');
            throw error.response?.status;
        }
    };

    useEffect(() => {
        switch (unverifiedAccountUiState) {
            case EUnverifiedAccountUiState.default:
                parseRouterState();
                break;
            case EUnverifiedAccountUiState.success:
                // local storage used to retrieve login link after verification
                putFullLoginLinkInLocalStorage();
                setShowToaster(true);
                setTimeout(() => {
                    setShowToaster(false);
                }, 5000);
                break;
        }
    }, [unverifiedAccountUiState]);

    return (
        <UnverifiedAccounContext.Provider
            value={{
                email,
                unverifiedAccountUiState,
                requestVerificationEmail,
                showToaster,
                submitVerifyPasscode,
                resendVerifyPasscode,
            }}
        >
            {children}
        </UnverifiedAccounContext.Provider>
    );
};

export default UnverifiedAccounContext;
